import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router";
import { useToast } from "../../components/ui/Toast";
import {
  CustomUrlEditor,
  emptyCustomUrlFormValues,
  type CustomUrlFormValues,
} from "./custom-url-edit/CustomUrlEditor";
import {
  CREATE_CUSTOM_URL,
  evictCustomUrlListings,
} from "./custom-url-edit/customUrlMutations";

interface CreateResponse {
  createCustomUrl: { id: string; slug: string } | null;
}

export function CustomUrlCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [create, createResult] = useMutation<CreateResponse>(
    CREATE_CUSTOM_URL,
    {
      refetchQueries: ["AllCustomUrlsForListing"],
      update: evictCustomUrlListings,
    },
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave(values: CustomUrlFormValues) {
    setSaveError(null);
    try {
      const result = await create({
        variables: {
          slug: values.slug,
          targetType: values.targetType,
          targetLandingPageId: values.targetLandingPageId,
          targetPath: values.targetPath,
          isActive: values.isActive,
        },
      });
      const created = result.data?.createCustomUrl;
      if (!created) throw new Error("Couldn't create custom URL");
      toast.push({
        kind: "success",
        title: `"${values.slug}" created`,
        description: values.isActive
          ? "Live on your site."
          : "Saved as inactive.",
      });
      navigate(`/website/custom-urls/${created.id}`, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setSaveError(message);
      toast.push({
        kind: "error",
        title: "Couldn't create URL",
        description: message,
      });
    }
  }

  return (
    <CustomUrlEditor
      mode="create"
      initialValues={emptyCustomUrlFormValues()}
      updatedAt={null}
      isSaving={createResult.loading}
      saveError={saveError}
      onSave={handleSave}
    />
  );
}
